var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { AddJoinCommonRequirementsWizardViewModel } from "../../../AddProjectJoinCommon/AddJoinCommonRequirementsWizardViewModel";
import { computed, observable, reaction } from "mobx";
import { deepCopy, isKeyOfObject, willNeverHappen } from "healthex-common/utils";
import { StudyTranslationImportType, StudyTranslationLanguage, } from "healthex-common/study.dtos";
import starsIcon from "../../../../Media/stars-icon-24x24.svg";
import alertCheckIcon from "../../../../Media/alert-check-large.svg";
import alertDangerIcon from "../../../../Media/alert-danger-large.svg";
import { SimpleDialogModel } from "../../../SimpleDialogModel";
import { AIGeneratedFieldsModel } from "../../../AddProjectJoinCommon/AIGeneratedFieldsModel";
import warningIcon from "../../../../Media/warning-icon-52x52.svg";
import successIcon from "../../../../Media/success-icon-52x52.svg";
import { downloadEnglishInformedConsentTranslationsAsCSV, forceExcludedPropertiesForTranslationDocumentList, getNumberOfRequiredTranslatableFields, getNumberOfTranslatableFields, getTranslatableFieldsFromProjectSections, getTranslatableFieldsFromStudy, importTranslationsDocument, translationReference, } from "../../../ProjectTranslationService";
import { NOT_LOADED } from "../../../../Infra/RemoteData";
import { ProjectPageName } from "healthex-common/project.data.dtos";
export class InformedConsentTranslationsWizardViewModel extends AddJoinCommonRequirementsWizardViewModel {
    constructor(opts) {
        var _a;
        super(opts.container, opts.container.informedConsentProjectTranslationsWizardUIManager, opts.viewType, opts.viewType === "FULL"
            ? "LANGUAGE_SELECTION"
            : opts.viewType === "DOCUMENT_IMPORT"
                ? "IMPORT_METHOD_DOCUMENT_TRANSLATIONS"
                : "IMPORT_METHOD_AI_GENERATION", {
            language: null,
            importMethod: null,
            translationsFile: null,
            translationsUrl: null,
        });
        this.confirmGoBackDialog = new SimpleDialogModel();
        this.translationDocumentImportInfo = null;
        this.translationsFileSucceeded = null;
        this.translationImportExtractedInfo = null;
        this.abortController = null;
        this._language = StudyTranslationLanguage.es;
        this.outOfDateImportedTranslations = {};
        this.aiGenerationStatus = {
            succeeded: null,
            numberOfTranslatedFields: 0,
            numberOfEnglishFields: 0,
            numberOfRequiredTranslatableFields: 0,
        };
        this.translationsImportProgressTimerId = null;
        this.translationsImportTimerProgress = 0;
        this.generateAITranslations = async () => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            if (this.study === null || this.study === NOT_LOADED)
                return;
            this.cleanupImportData();
            this.startProgressBar(0);
            const language = this.language;
            try {
                const study = deepCopy(this.study);
                const englishStudyTranslatableFields = (_a = getTranslatableFieldsFromStudy(deepCopy(study), this.availableTranslatableFields, StudyTranslationLanguage.en)) !== null && _a !== void 0 ? _a : {};
                const englishAdditionalSections = (_c = (_b = study.additionalSections) === null || _b === void 0 ? void 0 : _b.filter((section) => section.included && section.language === StudyTranslationLanguage.en)) !== null && _c !== void 0 ? _c : [];
                const englishSectionImages = (_d = study.studySectionImages) === null || _d === void 0 ? void 0 : _d.map((image) => image.images).flat().filter((image) => image.language === StudyTranslationLanguage.en && image.caption);
                const aiTranslationResponse = await this.studyManager.translateStudyFields(this.study.id, {
                    language,
                    studyTranslatableFields: englishStudyTranslatableFields,
                    additionalSections: englishAdditionalSections,
                    studySectionImages: englishSectionImages,
                });
                this.processingApisDone = true;
                if (!aiTranslationResponse) {
                    this.aiGenerationStatus.succeeded = false;
                    this.aiGenerationStatus.numberOfRequiredTranslatableFields = getNumberOfRequiredTranslatableFields(this.study, this.availableTranslatableFields, language);
                    this.cleanupImportProgress();
                    return;
                }
                const numberOfTranslatedFields = getNumberOfTranslatableFields(aiTranslationResponse.studyTranslatableFields, aiTranslationResponse.additionalSections, aiTranslationResponse.studySectionImages);
                if (numberOfTranslatedFields === 0) {
                    this.aiGenerationStatus.succeeded = false;
                    this.aiGenerationStatus.numberOfRequiredTranslatableFields = getNumberOfRequiredTranslatableFields(this.study, this.availableTranslatableFields, language);
                    this.cleanupImportProgress();
                }
                else {
                    const numberOfEnglishFields = getNumberOfTranslatableFields(englishStudyTranslatableFields, englishAdditionalSections, englishSectionImages);
                    this.aiGenerationStatus.numberOfTranslatedFields = numberOfTranslatedFields;
                    this.aiGenerationStatus.numberOfEnglishFields = numberOfEnglishFields;
                    this.aiGenerationStatus.succeeded = true;
                    this.progressBars.forEach((_, index) => {
                        this.setProgress(index, 100);
                        clearInterval(this.updateIntervals[index]);
                    });
                    const currentSectionImages = (_f = (_e = study.studySectionImages) === null || _e === void 0 ? void 0 : _e.map((image) => image.images).flat().filter((image) => image.caption)) !== null && _f !== void 0 ? _f : [];
                    const sectionImagesToSave = [];
                    (_g = aiTranslationResponse.studySectionImages) === null || _g === void 0 ? void 0 : _g.forEach((newImage) => {
                        var _a, _b, _c;
                        const currentTranslatedImage = currentSectionImages.find((currentImage) => currentImage.translatedFrom === newImage.translatedFrom && currentImage.language === language);
                        const currentEnglishImage = currentSectionImages.find((currentImage) => currentImage.id === newImage.translatedFrom && currentImage.language === StudyTranslationLanguage.en);
                        if (currentEnglishImage) {
                            sectionImagesToSave.push({
                                sectionName: currentEnglishImage.sectionName,
                                caption: (_a = newImage.caption) !== null && _a !== void 0 ? _a : "",
                                imageId: currentTranslatedImage === null || currentTranslatedImage === void 0 ? void 0 : currentTranslatedImage.id,
                                additionalSectionId: (_b = currentTranslatedImage === null || currentTranslatedImage === void 0 ? void 0 : currentTranslatedImage.additionalSectionId) !== null && _b !== void 0 ? _b : currentEnglishImage === null || currentEnglishImage === void 0 ? void 0 : currentEnglishImage.additionalSectionId,
                                translatedFrom: currentEnglishImage.id,
                                order: (_c = currentTranslatedImage === null || currentTranslatedImage === void 0 ? void 0 : currentTranslatedImage.order) !== null && _c !== void 0 ? _c : 0,
                            });
                        }
                    });
                    this.translationDocumentImportInfo = {
                        translatableFields: {
                            ...aiTranslationResponse.studyTranslatableFields,
                            language,
                            importType: StudyTranslationImportType.AI_GENERATION,
                            enabled: true,
                        },
                        additionalSections: (_h = aiTranslationResponse.additionalSections) === null || _h === void 0 ? void 0 : _h.map((section) => {
                            var _a, _b, _c, _d;
                            const currentTranslatedAdditionalSection = (_b = (_a = study.additionalSections) === null || _a === void 0 ? void 0 : _a.find((s) => section.translatedFrom === s.translatedFrom)) !== null && _b !== void 0 ? _b : null;
                            return {
                                ...currentTranslatedAdditionalSection,
                                language: (_c = currentTranslatedAdditionalSection === null || currentTranslatedAdditionalSection === void 0 ? void 0 : currentTranslatedAdditionalSection.language) !== null && _c !== void 0 ? _c : language,
                                included: (_d = currentTranslatedAdditionalSection === null || currentTranslatedAdditionalSection === void 0 ? void 0 : currentTranslatedAdditionalSection.included) !== null && _d !== void 0 ? _d : true,
                                ...section,
                            };
                        }),
                        sectionImages: sectionImagesToSave,
                        language,
                    };
                }
            }
            catch (error) {
                this.aiGenerationStatus.succeeded = false;
                this.aiGenerationStatus.numberOfRequiredTranslatableFields = getNumberOfRequiredTranslatableFields(this.study, this.availableTranslatableFields, language);
                this.cleanupImportProgress();
                console.log("Catch block of generate!!!", error);
            }
            finally {
                this.clearProtocolDocumentProgressIntervals();
            }
        };
        this.studyManager = opts.container.studyManager;
        this.addProjectUIManager = opts.addProjectUIManager;
        this.bannerUIManager = opts.container.bannerUIManager;
        this.persistence = opts.container.persistence;
        this.aiGeneratedFieldsModel = new AIGeneratedFieldsModel(this.studyManager, (update) => this.addProjectUIManager.informedConsentFormModel.updateInput(update));
        this._language = (_a = opts.language) !== null && _a !== void 0 ? _a : StudyTranslationLanguage.es;
        this.onImportUpdate = opts.onImportUpdate;
        this.onEnglishTranslationsDownloaded = opts.onEnglishTranslationsDownloaded;
    }
    run() {
        super.run();
        this.disposers.push(reaction(() => this.currentPage, async () => {
            if (this.currentPage === "IMPORT_METHOD_AI_GENERATION") {
                await this.generateAITranslations();
            }
            else if (this.currentPage === "IMPORT_METHOD_DOCUMENT_TRANSLATIONS_GENERATING") {
                this.importTranslationsDocument();
            }
        }, { fireImmediately: true }));
        this.disposers.push(reaction(() => this.formModel.input.importMethod, async (value) => {
            if (value === null)
                this.resetImportData();
        }, { fireImmediately: false }));
    }
    stop() {
        super.stop();
        this.clearTranslationsImportProgressTimer();
        this.disposers.forEach((disposer) => disposer());
        this.disposers = [];
    }
    get pages() {
        switch (this.viewType) {
            case "FULL": {
                const pages = [];
                pages.push("LANGUAGE_SELECTION", "IMPORT_METHOD");
                if (this.formModel.input.importMethod === "AI_GENERATION") {
                    pages.push("IMPORT_METHOD_AI_GENERATION");
                }
                else if (this.formModel.input.importMethod === "TRANSLATION_DOCUMENT") {
                    pages.push("IMPORT_METHOD_DOCUMENT_TRANSLATIONS", "IMPORT_METHOD_DOCUMENT_TRANSLATIONS_GENERATING");
                }
                return pages;
            }
            case "DOCUMENT_IMPORT": {
                const pages = [];
                pages.push("IMPORT_METHOD_DOCUMENT_TRANSLATIONS", "IMPORT_METHOD_DOCUMENT_TRANSLATIONS_GENERATING");
                return pages;
            }
            case "AI_IMPORT": {
                const pages = [];
                pages.push("IMPORT_METHOD_AI_GENERATION");
                return pages;
            }
        }
        return willNeverHappen(this.viewType);
    }
    get dialogTitleInfo() {
        switch (this.currentPage) {
            case "LANGUAGE_SELECTION":
                return { title: "Which language would you like to add?" };
            case "IMPORT_METHOD":
                return { title: "How would you like to translate the patient experience?" };
            case "IMPORT_METHOD_AI_GENERATION":
                if (this.aiGenerationStatus.succeeded === true) {
                    return {
                        title: this.isRetranslation ? "Retranslation complete" : "Translation complete",
                        subtitle: this.isRetranslation
                            ? "Continue to review and complete the retranslations. "
                            : "Continue to review and complete the translations. ",
                        icon: alertCheckIcon,
                    };
                }
                else if (this.aiGenerationStatus.succeeded === false) {
                    return {
                        title: "We couldn't translate your consent",
                        subtitle: "Continue to translate the consent manually.",
                        icon: alertDangerIcon,
                    };
                }
                return {
                    title: this.isRetranslation ? "Retranslating consent..." : "Translating consent...",
                    icon: starsIcon,
                };
            case "IMPORT_METHOD_AI_REGENERATION":
                return { title: "Retranslating informed consent..." };
            case "IMPORT_METHOD_DOCUMENT_TRANSLATIONS":
                return { title: "Upload translation template" };
            case "IMPORT_METHOD_DOCUMENT_TRANSLATIONS_GENERATING":
                if (this.translationsFileSucceeded === true) {
                    return {
                        title: "Translation template successfully uploaded",
                        subtitle: "Continue to review and complete the translations.",
                        icon: successIcon,
                    };
                }
                else if (this.translationsFileSucceeded === false) {
                    return {
                        title: "We couldn't upload your translation template",
                        subtitle: "Continue to translate the consent manually.",
                        icon: warningIcon,
                    };
                }
                else {
                    return { title: "Uploading translation template..." };
                }
        }
        return willNeverHappen(this.currentPage);
    }
    async initiateDocumentUpload() { }
    resetImportData() {
        this.translationDocumentImportInfo = null;
        this.formModel.input.importMethod = null;
        this.translationsFileSucceeded = null;
        this.formModel.input.translationsFile = null;
        this.translationImportExtractedInfo = null;
    }
    get isInformedConsentFilled() {
        return this.addProjectUIManager.isInformedConsentFilled;
    }
    get studyId() {
        return this.addProjectUIManager.studyId;
    }
    get language() {
        var _a, _b;
        return (_b = (_a = this.formModel.input.language) !== null && _a !== void 0 ? _a : this._language) !== null && _b !== void 0 ? _b : StudyTranslationLanguage.es;
    }
    get showBackButton() {
        return this.currentPageIndex > 0;
    }
    get nextButtonText() {
        switch (this.currentPage) {
            case "IMPORT_METHOD_DOCUMENT_TRANSLATIONS_GENERATING":
                return "Continue";
            case "IMPORT_METHOD_AI_GENERATION":
                return "Continue";
            default:
                return "Next";
        }
    }
    get nextButtonEnabled() {
        switch (this.currentPage) {
            case "LANGUAGE_SELECTION":
                return this.formModel.input.language !== null;
            case "IMPORT_METHOD":
                return this.formModel.input.importMethod !== null;
            case "IMPORT_METHOD_DOCUMENT_TRANSLATIONS":
                return this.formModel.input.translationsFile !== null;
            case "IMPORT_METHOD_DOCUMENT_TRANSLATIONS_GENERATING":
                if (this.translationsFileSucceeded !== null)
                    return true;
                return this.translationDocumentImportInfo !== null;
            case "IMPORT_METHOD_AI_GENERATION":
                if (this.aiGenerationStatus.succeeded === null)
                    return false;
                return true;
            case "IMPORT_METHOD_AI_REGENERATION":
                return this.formModel.input.importMethod !== null;
        }
        return willNeverHappen(this.currentPage);
    }
    get backDialogText() {
        switch (this.currentPage) {
            case "IMPORT_METHOD_AI_GENERATION":
                return "We aren't done generating translations. If you go back, all progress will be lost.";
            case "IMPORT_METHOD_AI_REGENERATION":
                return "We aren't done re-generating translations. If you go back, all progress will be lost.";
            case "IMPORT_METHOD_DOCUMENT_TRANSLATIONS_GENERATING":
                return "We aren't done importing details from your translation template. If you go back, all progress will be lost.";
            default:
                return null;
        }
        return null;
    }
    get translationsExist() {
        if (!this.study || this.study === NOT_LOADED)
            return false;
        return this.addProjectUIManager.allLanguageSectionTranslationProgressInfo.some((info) => info.progress > 0 && info.language === this.language);
    }
    get isRetranslation() {
        return this.translationsExist;
    }
    get importBannerInfo() {
        var _a, _b, _c, _d, _e, _f, _g;
        const banners = [];
        switch (this.currentPage) {
            case "IMPORT_METHOD_DOCUMENT_TRANSLATIONS":
                if (this.translationsExist) {
                    banners.push({
                        text: "Importing a new translation template will overwrite information already added. This may take up to 5 seconds. Please don't exit this page while importing.",
                        status: "warning",
                    });
                }
                else {
                    banners.push({ text: "This may take up to 5 seconds Please don't exit this page while uploading.", status: "info" });
                }
                break;
            case "IMPORT_METHOD_DOCUMENT_TRANSLATIONS_GENERATING": {
                if (this.translationsFileSucceeded === true) {
                    banners.push({
                        text: `We've successfully translated ${(_b = (_a = this.translationImportExtractedInfo) === null || _a === void 0 ? void 0 : _a.extracted) !== null && _b !== void 0 ? _b : 0} fields, and ${(_d = (_c = this.translationImportExtractedInfo) === null || _c === void 0 ? void 0 : _c.required) !== null && _d !== void 0 ? _d : 0} fields still require your input to complete the translation.`,
                        status: "success",
                        showIcon: true,
                    });
                }
                else if (this.translationsFileSucceeded === false) {
                    banners.push({
                        text: `${(_f = (_e = this.translationImportExtractedInfo) === null || _e === void 0 ? void 0 : _e.required) !== null && _f !== void 0 ? _f : 0} required fields still need your input to complete the translation.`,
                        status: "warning",
                        showIcon: true,
                    });
                }
                else {
                    banners.push({ text: "This may take up to 5 seconds. Please don't exit this page while importing.", status: "info" });
                }
                if ((_g = this.translationImportExtractedInfo) === null || _g === void 0 ? void 0 : _g.originalEnglishChangeDetected) {
                    banners.push({
                        text: "We’ve detected that some English content may have changed since this file was originally downloaded. We’ve flagged these sections. Review and update if needed.",
                        status: "warning",
                        showIcon: true,
                    });
                }
                break;
            }
            case "IMPORT_METHOD_AI_GENERATION": {
                if (this.aiGenerationStatus.succeeded) {
                    let bannerText = `We’ve successfully translated ${this.aiGenerationStatus.numberOfTranslatedFields} fields`;
                    const remainingFieldsToTranslate = Math.max(0, this.aiGenerationStatus.numberOfEnglishFields - this.aiGenerationStatus.numberOfTranslatedFields);
                    if (remainingFieldsToTranslate > 0) {
                        bannerText += `, ${remainingFieldsToTranslate} fields still need your input to complete the translation.`;
                    }
                    else {
                        bannerText += ".";
                    }
                    banners.push({
                        text: bannerText,
                        status: "success",
                        showIcon: true,
                    });
                }
                else if (this.aiGenerationStatus.succeeded === false) {
                    banners.push({
                        text: `${this.aiGenerationStatus.numberOfRequiredTranslatableFields} fields still need your input to complete the translation.`,
                        status: "warning",
                        showIcon: true,
                    });
                }
                else {
                    banners.push({ text: "This may take up to 3 minutes. Please don’t exit this page while loading.", status: "info" });
                }
                break;
            }
            default:
                return [];
        }
        return banners;
    }
    cleanupImportProgress() {
        this.clearProtocolDocumentProgressIntervals();
        this.clearProtocolDocumentProgress();
    }
    cleanupImportData() {
        this.translationDocumentImportInfo = null;
        this.processingApisDone = false;
        this.cleanupImportProgress();
        this.translationsFileSucceeded = null;
        this.formModel.input.translationsFile = null;
        this.translationImportExtractedInfo = null;
        this.aiGenerationStatus = {
            succeeded: null,
            numberOfTranslatedFields: 0,
            numberOfEnglishFields: 0,
            numberOfRequiredTranslatableFields: 0,
        };
        this.outOfDateImportedTranslations = {};
    }
    async goToPreviousPage() {
        if (this.currentPageIndex <= 0)
            return;
        if (this.backDialogText !== null) {
            const confirmed = await this.confirmGoBackDialog.showAndWait();
            if (!confirmed) {
                this.confirmGoBackDialog.open = false;
                return;
            }
            this.cleanupImportData();
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
        }
        super.goToPreviousPage();
    }
    async showGoBackConfirmationDialog() {
        const confirmed = await this.confirmGoBackDialog.showAndWait();
        if (confirmed) {
            this.confirmGoBackDialog.open = false;
        }
        return confirmed;
    }
    get nextButtonLoading() {
        return undefined;
    }
    get nextButtonChevronVisible() {
        return this.currentPage !== "IMPORT_METHOD";
    }
    updateProtocolDocument(iCFDocument) {
        this.formModel.input.translationsFile = iCFDocument;
    }
    updateTranslationFile(file) {
        this.formModel.input.translationsFile = file;
    }
    get uploadingProgressBarPercentage() {
        return this.progressBars[0];
    }
    get extractingProgressBarPercentage() {
        return this.progressBars[1];
    }
    get processingProgressBarPercentage() {
        return this.progressBars[2];
    }
    get study() {
        return this.addProjectUIManager.study;
    }
    async goToNextPage() {
        var _a, _b, _c;
        if (this.currentPage === "IMPORT_METHOD_DOCUMENT_TRANSLATIONS_GENERATING") {
            if (this.translationsFileSucceeded === false) {
                this.uiManager.importTranslationDialogVisible = false;
            }
            else if (this.translationsFileSucceeded === true && this.translationDocumentImportInfo) {
                await ((_a = this.onImportUpdate) === null || _a === void 0 ? void 0 : _a.call(this, {
                    ...this.translationDocumentImportInfo,
                }, this.outOfDateImportedTranslations));
            }
            this.uiManager.importTranslationDialogVisible = false;
            return;
        }
        if (this.currentPage === "IMPORT_METHOD_AI_GENERATION") {
            if (this.aiGenerationStatus.succeeded === null)
                return;
            if (this.aiGenerationStatus.succeeded && this.translationDocumentImportInfo) {
                await ((_b = this.onImportUpdate) === null || _b === void 0 ? void 0 : _b.call(this, {
                    ...this.translationDocumentImportInfo,
                }, {}));
                let bannerText = `We’ve successfully translated ${this.aiGenerationStatus.numberOfTranslatedFields} fields`;
                const remainingFieldsToTranslate = Math.max(0, this.aiGenerationStatus.numberOfEnglishFields - this.aiGenerationStatus.numberOfTranslatedFields);
                if (remainingFieldsToTranslate > 0) {
                    bannerText += `, ${remainingFieldsToTranslate} fields still need your input to complete the translation.`;
                }
                else {
                    bannerText += ".";
                }
                this.bannerUIManager.displayBanner({
                    text: bannerText,
                });
            }
            this.uiManager.importTranslationDialogVisible = false;
            return;
        }
        if (this.currentPage === "IMPORT_METHOD" && this.formModel.input.importMethod === StudyTranslationImportType.MANUAL) {
            await ((_c = this.onImportUpdate) === null || _c === void 0 ? void 0 : _c.call(this, {
                translatableFields: { language: this.language, importType: StudyTranslationImportType.MANUAL, enabled: true },
                language: this.language,
            }, {}));
            this.uiManager.importTranslationDialogVisible = false;
            return;
        }
        this.currentPage = this.pages[this.currentPageIndex + 1];
    }
    updateAIGenerationResults() {
    }
    importTranslationsDocument() {
        this.startTranslationsImportProgressTimer();
        const language = this.language;
        if (!this.formModel.input.translationsFile) {
            this.translationsFileSucceeded = false;
            return;
        }
        importTranslationsDocument(this.formModel.input.translationsFile, language, (importInfo) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            if (this.study === null || this.study === NOT_LOADED)
                return;
            if (!importInfo) {
                this.translationsFileSucceeded = false;
                return;
            }
            const study = deepCopy(this.study);
            const currentEnglishStudyTranslatableFields = getTranslatableFieldsFromStudy(study, this.availableTranslatableFields, StudyTranslationLanguage.en);
            const currentTranslationStudyTranslatableFields = getTranslatableFieldsFromStudy(study, this.availableTranslatableFields, language);
            if (!currentEnglishStudyTranslatableFields)
                return;
            const currentEnglishDiseaseConditions = (_a = currentEnglishStudyTranslatableFields.informedConsentPrimaryDiseaseOrConditions) !== null && _a !== void 0 ? _a : [];
            const currentEnglishTimelineVisits = (_b = currentEnglishStudyTranslatableFields.timelineVisits) !== null && _b !== void 0 ? _b : [];
            const currentEnglishAdditionalSections = (_d = (_c = study.additionalSections) === null || _c === void 0 ? void 0 : _c.filter((section) => section.included && section.language === StudyTranslationLanguage.en)) !== null && _d !== void 0 ? _d : [];
            const currentSectionImages = (_e = study.studySectionImages) !== null && _e !== void 0 ? _e : [];
            const currentTranslationDiseaseConditions = (_f = currentTranslationStudyTranslatableFields === null || currentTranslationStudyTranslatableFields === void 0 ? void 0 : currentTranslationStudyTranslatableFields.informedConsentPrimaryDiseaseOrConditions) !== null && _f !== void 0 ? _f : [];
            const currentTranslationTimelineVisits = (_g = currentTranslationStudyTranslatableFields === null || currentTranslationStudyTranslatableFields === void 0 ? void 0 : currentTranslationStudyTranslatableFields.timelineVisits) !== null && _g !== void 0 ? _g : [];
            const currentTranslationAdditionalSections = (_j = (_h = study.additionalSections) === null || _h === void 0 ? void 0 : _h.filter((section) => section.included && section.language === language)) !== null && _j !== void 0 ? _j : [];
            delete currentEnglishStudyTranslatableFields.informedConsentPrimaryDiseaseOrConditions;
            delete currentEnglishStudyTranslatableFields.timelineVisits;
            const originalEnglishStudyTranslatableFields = importInfo.originalEnglish.translatableFields;
            const originalEnglishDiseaseConditions = (_k = originalEnglishStudyTranslatableFields.informedConsentPrimaryDiseaseOrConditions) !== null && _k !== void 0 ? _k : [];
            const originalEnglishAdditionalSections = importInfo.originalEnglish.additionalSections;
            const originalEnglishTimelineVisits = (_l = originalEnglishStudyTranslatableFields.timelineVisits) !== null && _l !== void 0 ? _l : [];
            const originalEnglishSectionImages = (_m = importInfo.originalEnglish.sectionImages) !== null && _m !== void 0 ? _m : [];
            delete originalEnglishStudyTranslatableFields.informedConsentPrimaryDiseaseOrConditions;
            delete originalEnglishStudyTranslatableFields.timelineVisits;
            const translatedFields = importInfo.translations.translatableFields;
            const translatedDiseaseConditions = (_o = translatedFields.informedConsentPrimaryDiseaseOrConditions) !== null && _o !== void 0 ? _o : [];
            const translatedAdditionalSections = importInfo.translations.additionalSections;
            const translatedTimelineVisits = (_p = translatedFields.timelineVisits) !== null && _p !== void 0 ? _p : [];
            const translatedSectionImages = (_q = importInfo.translations.sectionImages) !== null && _q !== void 0 ? _q : [];
            delete translatedFields.informedConsentPrimaryDiseaseOrConditions;
            delete translatedFields.timelineVisits;
            this.outOfDateImportedTranslations = {};
            const validTranslations = {};
            let originalEnglishChangeDetected = false;
            Object.keys(translatedFields).forEach((key) => {
                if (isKeyOfObject(key, translationReference)) {
                    if (key === "enrollmentVideo") {
                        return;
                    }
                    const originalText = originalEnglishStudyTranslatableFields[key];
                    const currentText = currentEnglishStudyTranslatableFields[key];
                    if (originalText && currentText && originalText === currentText) {
                        ;
                        validTranslations[key] = translatedFields[key];
                    }
                    else {
                        originalEnglishChangeDetected = true;
                        this.outOfDateImportedTranslations[key] = true;
                    }
                }
            });
            const validDiseaseAndCondition = [];
            let numberOfNotTranslatedDiseaseAndConditions = 0;
            let diseaseAndConditionsEnglishChanged = originalEnglishDiseaseConditions.length !== currentEnglishDiseaseConditions.filter((condition) => !!condition).length;
            currentEnglishDiseaseConditions.forEach((condition, index) => {
                var _a;
                const originalEnglishConditionIndex = originalEnglishDiseaseConditions.findIndex((originalCondition) => originalCondition === condition);
                const originalEnglishTranslatedCondition = translatedDiseaseConditions[originalEnglishConditionIndex];
                const translatedCondition = translatedDiseaseConditions[originalEnglishConditionIndex];
                if (condition && originalEnglishTranslatedCondition && translatedCondition) {
                    validDiseaseAndCondition.push(translatedCondition);
                }
                else {
                    validDiseaseAndCondition.push((_a = currentTranslationDiseaseConditions[index]) !== null && _a !== void 0 ? _a : "");
                    numberOfNotTranslatedDiseaseAndConditions += 1;
                    if (originalEnglishTranslatedCondition && condition && originalEnglishTranslatedCondition !== condition) {
                        diseaseAndConditionsEnglishChanged = true;
                        this.outOfDateImportedTranslations.informedConsentPrimaryDiseaseOrConditions = true;
                    }
                }
            });
            const validTimelineVisits = [];
            let numberOfNotTranslatedTimelineVisits = 0;
            let timelineVisitsEnglishChanged = originalEnglishTimelineVisits.length !==
                currentEnglishTimelineVisits.filter((timeline) => !!timeline.period && !!timeline.description).length;
            currentEnglishTimelineVisits.forEach((timeline) => {
                var _a;
                const originalEnglishTimelineIndex = originalEnglishTimelineVisits.findIndex((originalTimeline) => originalTimeline.period === timeline.period && originalTimeline.description === timeline.description);
                const originalEnglishTimeline = originalEnglishTimelineVisits[originalEnglishTimelineIndex];
                const translationTimeline = translatedTimelineVisits[originalEnglishTimelineIndex];
                if ((timeline === null || timeline === void 0 ? void 0 : timeline.period) &&
                    (timeline === null || timeline === void 0 ? void 0 : timeline.description) &&
                    (originalEnglishTimeline === null || originalEnglishTimeline === void 0 ? void 0 : originalEnglishTimeline.period) &&
                    (originalEnglishTimeline === null || originalEnglishTimeline === void 0 ? void 0 : originalEnglishTimeline.description) &&
                    (translationTimeline === null || translationTimeline === void 0 ? void 0 : translationTimeline.period) &&
                    (translationTimeline === null || translationTimeline === void 0 ? void 0 : translationTimeline.description)) {
                    validTimelineVisits.push(translationTimeline);
                }
                else {
                    validTimelineVisits.push((_a = currentTranslationTimelineVisits[originalEnglishTimelineIndex]) !== null && _a !== void 0 ? _a : { period: "", description: "" });
                    numberOfNotTranslatedTimelineVisits += 1;
                    if ((timeline === null || timeline === void 0 ? void 0 : timeline.period) &&
                        (timeline === null || timeline === void 0 ? void 0 : timeline.description) &&
                        (originalEnglishTimeline === null || originalEnglishTimeline === void 0 ? void 0 : originalEnglishTimeline.period) &&
                        (originalEnglishTimeline === null || originalEnglishTimeline === void 0 ? void 0 : originalEnglishTimeline.description) &&
                        (timeline.period !== originalEnglishTimeline.period || timeline.description !== originalEnglishTimeline.description)) {
                        timelineVisitsEnglishChanged = true;
                        this.outOfDateImportedTranslations.timelineVisits = true;
                    }
                }
            });
            const validAdditionalSections = [];
            let additionalSectionEnglishChanged = originalEnglishAdditionalSections.length !== currentEnglishAdditionalSections.length;
            currentEnglishAdditionalSections.forEach((currentSection) => {
                var _a;
                const originalEnglishAdditionSection = originalEnglishAdditionalSections.find((originalSection) => originalSection.id === currentSection.id);
                if (originalEnglishAdditionSection !== undefined &&
                    originalEnglishAdditionSection.title === currentSection.title &&
                    originalEnglishAdditionSection.description === currentSection.description) {
                    const newTranslatedAdditionalSection = translatedAdditionalSections.find((section) => section.id === currentSection.id);
                    if (newTranslatedAdditionalSection) {
                        const currentTranslatedAdditionalSection = currentTranslationAdditionalSections.find((section) => section.translatedFrom === newTranslatedAdditionalSection.id);
                        if (currentTranslatedAdditionalSection) {
                            validAdditionalSections.push({
                                id: currentTranslatedAdditionalSection.id,
                                translatedFrom: (_a = currentTranslatedAdditionalSection.translatedFrom) !== null && _a !== void 0 ? _a : undefined,
                                title: newTranslatedAdditionalSection.title,
                                description: newTranslatedAdditionalSection.description,
                            });
                        }
                        else {
                            validAdditionalSections.push({
                                id: crypto.randomUUID(),
                                title: newTranslatedAdditionalSection.title,
                                description: newTranslatedAdditionalSection.description,
                                translatedFrom: currentSection.id,
                            });
                        }
                    }
                }
                else {
                    additionalSectionEnglishChanged = true;
                    this.outOfDateImportedTranslations[`additionalInformation_${currentSection.id}`] = true;
                }
            });
            const validSectionImages = [];
            const allCurrentSectionImages = currentSectionImages.map((image) => image.images).flat();
            const allCurrentEnglishSectionImages = allCurrentSectionImages.filter((image) => image.language === StudyTranslationLanguage.en && !!image.caption);
            let sectionImagesEnglishChanged = false;
            currentSectionImages.forEach((currentGroupedSection) => {
                const { sectionName, images } = currentGroupedSection;
                const groupedSectionImages = images.filter((image) => image.language === StudyTranslationLanguage.en && !!image.caption);
                groupedSectionImages.forEach((currentImage) => {
                    var _a, _b, _c;
                    const originalEnglishSection = originalEnglishSectionImages.find((originalSection) => originalSection.sectionName === sectionName &&
                        originalSection.images.some((image) => image.id === currentImage.id));
                    if (originalEnglishSection) {
                        if (groupedSectionImages.length !== originalEnglishSection.images.length) {
                            sectionImagesEnglishChanged = true;
                        }
                        const originalEnglishImageIndex = originalEnglishSection.images.findIndex((image) => image.id === currentImage.id);
                        if (originalEnglishImageIndex >= 0) {
                            const englishImage = originalEnglishSection.images[originalEnglishImageIndex];
                            if (englishImage.caption === currentImage.caption) {
                                const translatedImage = (_a = translatedSectionImages.find((section) => section.sectionName === sectionName)) === null || _a === void 0 ? void 0 : _a.images[originalEnglishImageIndex];
                                if (translatedImage === null || translatedImage === void 0 ? void 0 : translatedImage.caption) {
                                    validSectionImages.push({
                                        sectionName,
                                        caption: translatedImage.caption,
                                        imageId: (_c = (_b = allCurrentSectionImages.find((image) => image.language === language && image.translatedFrom === currentImage.id)) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : undefined,
                                        additionalSectionId: translatedImage.additionalSectionId,
                                        translatedFrom: translatedImage.id,
                                        order: currentImage.order,
                                    });
                                }
                            }
                            else {
                                sectionImagesEnglishChanged = true;
                                this.outOfDateImportedTranslations[`sectionImages_${currentImage.id}`] = true;
                            }
                        }
                    }
                });
            });
            const numberOfCurrentStudyTranslatableFields = Object.keys(currentEnglishStudyTranslatableFields).length +
                (currentEnglishDiseaseConditions.length - numberOfNotTranslatedDiseaseAndConditions) +
                currentEnglishAdditionalSections.length +
                (currentEnglishTimelineVisits.length - numberOfNotTranslatedTimelineVisits) +
                allCurrentEnglishSectionImages.length -
                forceExcludedPropertiesForTranslationDocumentList.length;
            const numberOfExtractedfTranslations = Object.keys(validTranslations).length +
                (validDiseaseAndCondition.length - numberOfNotTranslatedDiseaseAndConditions) +
                validAdditionalSections.length +
                (validTimelineVisits.length - numberOfNotTranslatedTimelineVisits) +
                validSectionImages.length;
            this.translationImportExtractedInfo = {
                extracted: numberOfExtractedfTranslations,
                required: Math.max(0, numberOfCurrentStudyTranslatableFields - numberOfExtractedfTranslations),
                originalEnglishChangeDetected: originalEnglishChangeDetected ||
                    diseaseAndConditionsEnglishChanged ||
                    additionalSectionEnglishChanged ||
                    timelineVisitsEnglishChanged ||
                    sectionImagesEnglishChanged,
            };
            this.translationsFileSucceeded = numberOfExtractedfTranslations > 0;
            if (validDiseaseAndCondition.length)
                validTranslations.informedConsentPrimaryDiseaseOrConditions = validDiseaseAndCondition;
            if (validTimelineVisits.length)
                validTranslations.timelineVisits = validTimelineVisits;
            this.translationDocumentImportInfo = {
                translatableFields: {
                    ...validTranslations,
                    language,
                    importType: StudyTranslationImportType.TRANSLATION_DOCUMENT,
                    enabled: true,
                },
                additionalSections: validAdditionalSections.map((section) => ({
                    ...section,
                    language,
                    included: true,
                })),
                sectionImages: validSectionImages,
                language,
            };
        });
    }
    goToTranslationsHelpPage() {
        window.open("https://www.healthex.io/", "_blank");
    }
    downloadEnglishTranslations() {
        if (this.study === null || this.study === NOT_LOADED)
            return;
        downloadEnglishInformedConsentTranslationsAsCSV(deepCopy(this.study), this.availableTranslatableFields);
        this.persistence.setEnglishStringsDownloaded(this.study.id);
        this.addProjectUIManager.didDownloadTranslationsThisSession = true;
        this.onEnglishTranslationsDownloaded();
        this.uiManager.importTranslationDialogVisible = false;
    }
    get translationsImportProgress() {
        return this.translationsFileSucceeded !== null ? 100 : this.translationsImportTimerProgress;
    }
    get availableTranslatableFields() {
        var _a, _b;
        if (!this.addProjectUIManager.projectPages)
            return [];
        return getTranslatableFieldsFromProjectSections((_b = (_a = this.addProjectUIManager.projectPages.pages.find((page) => page.pageName === ProjectPageName.PATIENT_EXPERIENCE)) === null || _a === void 0 ? void 0 : _a.sections) !== null && _b !== void 0 ? _b : []);
    }
    clearTranslationsImportProgress() {
        this.clearTranslationsImportProgressTimer();
        this.translationsImportTimerProgress = 0;
    }
    clearTranslationsImportProgressTimer() {
        if (this.translationsImportProgressTimerId !== null) {
            window.clearInterval(this.translationsImportProgressTimerId);
        }
    }
    startTranslationsImportProgressTimer() {
        this.clearTranslationsImportProgress();
        this.translationsImportProgressTimerId = window.setInterval(() => {
            this.translationsImportTimerProgress = Math.min(80, this.translationsImportTimerProgress + 20);
            if (this.translationsImportTimerProgress >= 80) {
                this.clearTranslationsImportProgressTimer();
            }
        }, 300);
    }
}
__decorate([
    observable
], InformedConsentTranslationsWizardViewModel.prototype, "translationDocumentImportInfo", void 0);
__decorate([
    observable
], InformedConsentTranslationsWizardViewModel.prototype, "translationsFileSucceeded", void 0);
__decorate([
    observable
], InformedConsentTranslationsWizardViewModel.prototype, "translationImportExtractedInfo", void 0);
__decorate([
    observable
], InformedConsentTranslationsWizardViewModel.prototype, "_language", void 0);
__decorate([
    observable
], InformedConsentTranslationsWizardViewModel.prototype, "aiGenerationStatus", void 0);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "pages", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "dialogTitleInfo", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "isInformedConsentFilled", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "studyId", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "language", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "showBackButton", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "nextButtonText", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "nextButtonEnabled", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "backDialogText", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "translationsExist", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "isRetranslation", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "importBannerInfo", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "nextButtonLoading", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "nextButtonChevronVisible", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "uploadingProgressBarPercentage", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "extractingProgressBarPercentage", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "processingProgressBarPercentage", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "study", null);
__decorate([
    observable
], InformedConsentTranslationsWizardViewModel.prototype, "translationsImportTimerProgress", void 0);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "translationsImportProgress", null);
__decorate([
    computed
], InformedConsentTranslationsWizardViewModel.prototype, "availableTranslatableFields", null);
